import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'
import { formatCents } from '../../lib/money'
import type { TimeEntryDto, TimeEntryListResponse } from '@toga/shared'

interface Props {
  invoiceId: string
  clientId: string
  onClose: () => void
  onImported: () => void | Promise<void>
}

export default function ImportTimeEntriesModal({
  invoiceId,
  clientId,
  onClose,
  onImported,
}: Props) {
  const [items, setItems] = useState<TimeEntryDto[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [groupByCase, setGroupByCase] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ clientId, status: 'UNBILLED' })
      const resp = await api.get<TimeEntryListResponse>(`/time-entries?${params.toString()}`)
      setItems(resp.items)
      // Por defecto, todos seleccionados.
      setSelected(new Set(resp.items.map((i) => i.id)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => {
    void load()
  }, [load])

  const grouped = useMemo(() => {
    const m = new Map<string, { caseCode: string; caseTitle: string; items: TimeEntryDto[] }>()
    for (const i of items) {
      const g = m.get(i.caseId) ?? { caseCode: i.caseCode, caseTitle: i.caseTitle, items: [] }
      g.items.push(i)
      m.set(i.caseId, g)
    }
    return Array.from(m.entries()).map(([caseId, g]) => ({ caseId, ...g }))
  }, [items])

  const selectedItems = items.filter((i) => selected.has(i.id))
  const totalMinutes = selectedItems.reduce((a, b) => a + b.durationMinutes, 0)
  const totalAmount = selectedItems.reduce((a, b) => a + b.amountCents, 0)

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleGroup = (group: { items: TimeEntryDto[] }) => {
    const allSelected = group.items.every((i) => selected.has(i.id))
    setSelected((prev) => {
      const next = new Set(prev)
      for (const it of group.items) {
        if (allSelected) next.delete(it.id)
        else next.add(it.id)
      }
      return next
    })
  }

  const onImport = async () => {
    if (selectedItems.length === 0) {
      setError('Selecciona al menos una entrada')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.post(`/invoices/${invoiceId}/import-time-entries`, {
        timeEntryIds: Array.from(selected),
        groupByCase,
      })
      await onImported()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open onClose={onClose} title="Importar tiempos no facturados" maxWidth="max-w-2xl">
      {loading ? (
        <div className="t-muted flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm t-faint py-4 text-center">
          No hay tiempos sin facturar para este cliente.
        </p>
      ) : (
        <>
          <div className="text-xs t-muted mb-3">
            {items.length} entrada{items.length !== 1 ? 's' : ''} sin facturar de este cliente.
          </div>

          <div className="max-h-80 overflow-y-auto space-y-3 mb-3">
            {grouped.map((g) => {
              const allSelected = g.items.every((i) => selected.has(i.id))
              return (
                <div key={g.caseId} className="border border-base rounded-lg p-2">
                  <label className="flex items-center gap-2 text-sm font-medium t-primary">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => toggleGroup(g)}
                      className="accent-toga-500"
                    />
                    <span className="font-mono text-toga-300">{g.caseCode}</span>
                    <span className="truncate">{g.caseTitle}</span>
                  </label>
                  <ul className="mt-1 ml-6 space-y-1">
                    {g.items.map((e) => (
                      <li key={e.id} className="flex items-start gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={selected.has(e.id)}
                          onChange={() => toggle(e.id)}
                          className="mt-0.5 accent-toga-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="t-primary truncate">{e.description}</div>
                          <div className="t-faint">
                            {new Date(e.performedOn).toLocaleDateString('es-ES')} ·{' '}
                            {formatDuration(e.durationMinutes)} · {e.userName}
                          </div>
                        </div>
                        <div className="font-mono t-secondary">{formatCents(e.amountCents)}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>

          <label className="flex items-center gap-2 text-sm t-secondary mb-3">
            <input
              type="checkbox"
              checked={groupByCase}
              onChange={(e) => setGroupByCase(e.target.checked)}
              className="accent-toga-500"
            />
            Agrupar todas las entradas de cada expediente en una sola línea
          </label>

          <div className="surface-deep rounded p-3 text-sm flex justify-between items-baseline mb-3">
            <span className="t-muted">Seleccionado:</span>
            <span className="t-primary font-semibold">
              {selectedItems.length} entrada{selectedItems.length !== 1 ? 's' : ''} ·{' '}
              {formatDuration(totalMinutes)} · {formatCents(totalAmount)}
            </span>
          </div>

          {error && <p className="text-xs text-rose-300 mb-2">{error}</p>}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => void onImport()}
              disabled={busy || selectedItems.length === 0}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              Importar
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}h ${String(m).padStart(2, '0')}m`
}
